<script setup lang="ts">
import type { Dott } from './ProductImageTags.client.vue'

const t2 = useT2({
  'View Bundle': 'Peržiūrėti rinkinį',
  Save: 'Sutaupyk',
})

const props = defineProps<{
  glow?: boolean
  item: gen.SellerBundleFragment
  listings: ReturnType<typeof useGetListings> | None
  profiles: gen.SellerProfileFragment[] | None
}>()

function dotEqual(a: Dott | None, b: Dott | None): boolean {
  return helpers.isDef(a?.x) && a?.x === b?.x && a?.y === b?.y
}

const data = shallowReactive({
  tag: undefined as undefined | Dott,
  render: false,
  open: false,
})
function openSet(on: boolean) {
  if (on && !data.render) data.render = true
  data.open = on
}

const tags = computed(() => {
  const { listings, item } = props
  return helpers.arr(item.imageTagged?.tags, (dot) => {
    const listing = listings?.getOneById(dot.productId)
    return listing ? { listing, dot, img: getListingImg(listing) } : undefined
  })
})
</script>

<template>
  <div tabindex="0" :class="[$style.ui, data.open && $style.on, tags.length && $style.tags, { glow }]" @click="openSet(!data.open)">
    <ProductBundleImage sizes="220px" :image-tagged="item.imageTagged" :class="$style.img" />

    <div v-if="item.title?.value" :class="$style.title">{{ item.title?.value }}</div>
    <div :class="[$style.open, item.percentValue && $style.openA]">
      <span v-if="item.percentValue" :class="$style.open1">{{ ts(t2('Save'), `${item.percentValue}%`) }}</span>
      <span :class="$style.open2">{{ t2('View Bundle') }}<IconExpand class="_svg16" /></span>
    </div>

    <div v-if="tags.length" :class="$style.box">
      <div :class="[$style.side, $clsClass('p-bundle')]">
        <BaseImage
          v-for="item2 in tags"
          :key="item2.listing.id"
          :class="[$style.sideImg, dotEqual(data.tag, item2.dot) && 'on']"
          sizes="120px"
          :img="item2.img"
          @mouseenter="data.tag = item2.dot"
          @mouseleave="data.tag = undefined"
        >
          <span :class="$style.price" class="_mono">{{ item2.listing?._values?._uiPrice?.price.priceForHumans }}</span>
        </BaseImage>
      </div>
    </div>

    <LazyProductBundleForm v-if="data.render" v-model="data.open" :bundle="item" />
  </div>
</template>

<style module>
.ui {
  composes: global(_relative);
  composes: global(_flex);

  cursor: pointer;
  --w: 2px;
  --side: 90px;
  --br: 0;
}

.on,
:global(.d) .ui:hover,
.ui:active {
  transition: z-index 0ms linear 0ms;
  z-index: 1;
}

.box {
  composes: global(_absolute);
  composes: global(_inset);
  composes: global(_br9);
  composes: global(_bg0);

  transition: width 180ms var(--tran2);
}

.tags.on .box,
.tags:active .box,
:global(.d) .tags:hover .box {
  width: calc(100% + var(--w) + var(--side));
  box-shadow:
    0 0 0 1px var(--black-5),
    var(--shadow-preview);
  transition: width 180ms var(--tran2);
}

.title {
  composes: global(_absolute);
  composes: global(_hm28);

  composes: global(_fw-s);
  composes: global(_fs09);
  composes: global(_fw-l);
  composes: global(_pt04);
  composes: global(_pb04);
  composes: global(_pl1);
  composes: global(_pr1);
  composes: global(_ls-xs);

  composes: global(_ctr);
  composes: global(_hidden);
  composes: global(_bg0);
  composes: global(_br6);

  left: 5px;
  bottom: 5px;
  font-style: italic;
  z-index: 2;
  composes: global(_c2);
}

.open {
  composes: global(_flexblock);

  composes: global(_absolute);
  composes: global(_hm34);
  composes: global(_fs09);
  composes: global(_fw-xl);
  composes: global(_ls-xs);

  composes: global(_br99);
  composes: global(_b0);

  composes: global(_pr1);
  composes: global(_pl14);
  composes: global(_hidden);
  composes: global(_bg0);

  top: 10px;
  left: 10px;

  z-index: 2;
  transition: opacity 180ms var(--tran2);
  opacity: 0.01;
}

.openBase {
  align-items: center;
  composes: global(_gap1);
}
.openA {
  composes: global(_opacity1);
  --open1: flex;
  --open2: none;
}
:global(.d) .ui:hover .openA,
.ui:active .openA,
.on .openA {
  --open1: none;
  --open2: flex;
}
.open1 {
  composes: openBase;
  display: var(--open1, flex);
}
.open2 {
  composes: openBase;
  display: var(--open2, flex);
}

:global(.d) .ui:hover .open,
.ui:active .open,
.on .open {
  opacity: 1;
  border-color: var(--blue7);
  box-shadow: 0 0 0 2px var(--blue8);
}

.img {
  composes: global(_relative);
  composes: global(_h100p);
  composes: global(_br9);
  composes: global(_hidden);

  cursor: pointer;
  z-index: 1;
  border: 2px solid var(--bg);
}

.on .img {
  border-color: var(--bg);
}

.side {
  composes: global(_absolute);
  composes: global(_col);
  composes: global(_pt04);
  composes: global(_pr04);
  composes: global(_pb04);
  composes: global(_pl04);
  composes: global(_gap08);
  composes: global(_hidden);

  width: var(--side);
  top: 2px;
  bottom: 2px;
  right: 2px;
  z-index: 1;
  transition: transform 180ms var(--tran2);
  --br2: calc(var(--br) - 2px);
  border-radius: 0 var(--br2) var(--br2) 0;
}

.side::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  width: calc(100% + 20px);
  height: calc(100% + 20px);
  transition: background 220ms var(--tran1);
  background: hsla(0, 0%, 90%, 0.25);
  filter: blur(4px);
}
:global(.d) .ui:hover .side::before,
.ui:active .side::before,
.on .side::before {
  opacity: 0;
}
.sideImg {
  composes: global(_relative);
  composes: global(_br9);
}
.sideImg:global(.on) {
  box-shadow:
    0 0 0 2px var(--bg),
    0 0 0 3px var(--black-45);
}
.price {
  composes: global(_absolute);
  composes: global(_fs08);

  composes: global(_br99);

  composes: global(_pt02);
  composes: global(_pb02);
  composes: global(_pl1);
  composes: global(_pr1);

  composes: global(_c1);

  right: 0px;
  bottom: 0px;
  background: hsla(240, 33.3%, 99%, 0.965);
}
.ui:global(.glow) {
  padding: 2px;
}
.ui:global(.glow) .img {
  border: 1px solid var(--bg);
}

:global(.d) .ui:global(.glow):hover .img,
.ui:global(.glow):active .img,
.on:global(.glow) .img {
  border: 1px solid var(--blue3);
  border-color: var(--blue7);
  box-shadow: 0 0 0 2px var(--blue8);
}
</style>
